import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Download, Trash2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import DeadlineTracker from "../components/DeadlineTracker";
import useLocalState from "../lib/useLocalState";

const OVERLINE_CLS = "text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const H2_CLS = "font-serif text-2xl md:text-3xl text-ink leading-tight tracking-tight mt-14 mb-5";
const LABEL_CLS = "block text-xs font-accent uppercase tracking-[0.14em] text-ink-muted mb-1.5";
const INPUT_CLS =
  "w-full bg-white border border-rule px-3 py-2.5 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:border-ink transition-colors";

function todayLondon() {
  return new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function buildSar({ name, address, dob, la, ref, extras }) {
  const nameLine = name || "[Your full name]";
  const addressLine = address ? address.trim() : "[Your address]";
  const dobLine = dob || "[Your date of birth, dd/mm/yyyy]";
  const laLine = la || "[Local authority name]";
  const refLine = ref ? `\nCase / reference: ${ref}` : "";
  const extraPara = extras
    ? `\n\nI would also like the following, if held:\n${extras}\n`
    : "";
  return `From: ${nameLine}
${addressLine}
Date of birth: ${dobLine}${refLine}

To: The Data Protection Officer
${laLine}

Date: ${todayLondon()}

Dear Data Protection Officer,

Subject Access Request under Article 15 UK GDPR / Data Protection Act 2018

I am writing to make a subject access request for all personal data you hold about me.

Please treat this as a formal subject access request. I confirm my identity as the person named above.

For the avoidance of doubt, I am requesting all personal data held across every part of the authority, including — but not limited to — Children's Social Care, Legal Services, Complaints, Information Governance, the Multi-Agency Safeguarding Hub (MASH), any Independent Reviewing Officer records, and any records held by contracted providers on your behalf.

The material I am asking to see specifically includes:
  • case notes, chronologies and running records;
  • all assessments (initial, section 47, single, parenting, viability, and any expert reports commissioned);
  • minutes of strategy discussions, child protection conferences, core group meetings, LAC reviews and any panel meetings;
  • internal and external emails and instant messages that concern me;
  • records of telephone calls and file notes;
  • any legal correspondence which is not properly the subject of legal professional privilege on a document-by-document basis;
  • the referral itself and any referral information sent by third parties.${extraPara}

I would like the information provided electronically, in a commonly used format.

If you rely on any exemption to withhold material, please state, for each item withheld: the specific exemption relied on, the document to which it is applied, and the reasoning. Please do not redact whole documents where only part is subject to an exemption. Please do not withhold professional information given by staff acting in a working capacity as third-party personal data.

I understand you have one calendar month to respond, and that any extension of up to two further months must be notified to me within that first month, with reasons.

Yours faithfully,

${nameLine}
`;
}

function buildFoi({ name, la, topics }) {
  const nameLine = name || "[Your full name]";
  const laLine = la || "[Local authority name]";
  const topicsBlock = topics
    ? topics
    : `  • the local authority's written thresholds for opening section 47 enquiries;
  • the local authority's policy on pre-proceedings (Public Law Outline) meetings;
  • the terms of reference of any legal or resource panel that decides whether to issue care proceedings;
  • any protocol or memorandum of understanding with the local police force in relation to strategy discussions;
  • training materials issued to social workers in the last three years on working with parents and on record-keeping;
  • the contracts and total spend, over the last three financial years, with any independent social work providers and independent psychologists commissioned by the authority.`;
  return `From: ${nameLine}
To: Freedom of Information / Information Governance Team
${laLine}

Date: ${todayLondon()}

Dear Information Rights Team,

Freedom of Information Request under the Freedom of Information Act 2000

I am writing to make a request for recorded information under the Freedom of Information Act 2000. To be clear, this is not a subject access request; I am asking about non-personal, recorded information held by the authority.

Please provide me with copies of the following, if held:

${topicsBlock}

Where any of the above is available on your website, a link is sufficient. Where information is withheld, please identify the specific statutory exemption relied on for each item, and — where the exemption is qualified — set out your public interest reasoning.

I understand you have twenty working days to respond. If you consider that any part of my request is not valid under the Act (for example, because you regard it as personal data engaging Section 40), please tell me and treat that part as, in the alternative, a separate subject access request.

Yours faithfully,

${nameLine}
`;
}

function CopyButton({ text, kind }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // no-op
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`copy-${kind}-button`}
      className="inline-flex items-center gap-2 border border-ink/30 text-ink px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
    >
      {copied ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.75} />}
      {copied ? "Copied" : "Copy to clipboard"}
    </button>
  );
}

function DownloadButton({ text, filename, kind }) {
  const onClick = () => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`download-${kind}-button`}
      className="inline-flex items-center gap-2 bg-ink text-linen px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:bg-river transition-colors"
    >
      <Download size={13} strokeWidth={1.75} />
      Download .txt
    </button>
  );
}

function TemplateCard({ title, blurb, text, kind, filename }) {
  return (
    <div className="border border-rule bg-white p-6 md:p-8">
      <div className={OVERLINE_CLS}>{title.split(" ")[0]} template</div>
      <h3 className="font-serif text-2xl md:text-3xl text-ink mt-2 mb-3 tracking-tight leading-tight">
        {title}
      </h3>
      <p className="text-sm text-ink-muted mb-5 leading-relaxed">{blurb}</p>
      <textarea
        readOnly
        value={text}
        data-testid={`template-${kind}-textarea`}
        rows={16}
        className="w-full bg-linen-alt border border-rule px-4 py-3 text-[13px] leading-relaxed font-mono text-ink resize-y focus:outline-none focus:border-ink"
        onFocus={(e) => e.target.select()}
      />
      <div className="mt-4 flex flex-wrap gap-3">
        <CopyButton text={text} kind={kind} />
        <DownloadButton text={text} filename={filename} kind={kind} />
      </div>
    </div>
  );
}

export default function RecordsTemplatesPage() {
  const [form, setForm, clearForm] = useLocalState("yana:tpl:v1", {
    name: "",
    address: "",
    dob: "",
    la: "",
    ref: "",
    extras: "",
    foiTopics: "",
  });
  const { name, address, dob, la, ref, extras, foiTopics } = form;
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const sar = useMemo(
    () => buildSar({ name, address, dob, la, ref, extras }),
    [name, address, dob, la, ref, extras]
  );
  const foi = useMemo(
    () => buildFoi({ name, la, topics: foiTopics }),
    [name, la, foiTopics]
  );

  const hasSaved = !!(name || address || dob || la || ref || extras || foiTopics);

  return (
    <div data-testid="records-templates-page">
      <PageMeta
        title="SAR and FOI email templates"
        description="Two ready-to-send email templates — a Subject Access Request and a Freedom of Information Request — pre-filled with your name, address and local authority. Copy, paste, and send."
        path="/understanding/records/templates"
      />
      <PageHeader
        overline="Records · Templates"
        title="Two emails you can send today."
        lede="Fill in a few boxes. We build the emails for you. Anything you type stays on your own device — it is saved in this browser so you don't have to retype it next time, and it never leaves your machine."
      />

      <article className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
          <Link
            to="/understanding/records"
            data-testid="templates-back-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to the full guide
          </Link>
          <LastReviewed path="/understanding/records/templates" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Form column */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <div className="border border-rule bg-linen-alt p-6 md:p-7">
              <div className={OVERLINE_CLS}>Your details</div>
              <p className="mt-2 text-xs text-ink-muted leading-relaxed">
                Saved only in this browser, never sent anywhere. Clear them any time.
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <label className={LABEL_CLS} htmlFor="tpl-name">
                    Your full name
                  </label>
                  <input
                    id="tpl-name"
                    data-testid="tpl-name"
                    className={INPUT_CLS}
                    value={name}
                    onChange={update("name")}
                    placeholder="e.g. Sarah Ahmed"
                  />
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="tpl-address">
                    Your address
                  </label>
                  <textarea
                    id="tpl-address"
                    data-testid="tpl-address"
                    className={INPUT_CLS}
                    value={address}
                    onChange={update("address")}
                    placeholder={"12 Example Street\nOldtown\nOX1 2AB"}
                    rows={3}
                  />
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="tpl-dob">
                    Date of birth
                  </label>
                  <input
                    id="tpl-dob"
                    data-testid="tpl-dob"
                    className={INPUT_CLS}
                    value={dob}
                    onChange={update("dob")}
                    placeholder="01/06/1985"
                  />
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="tpl-la">
                    Local authority
                  </label>
                  <input
                    id="tpl-la"
                    data-testid="tpl-la"
                    className={INPUT_CLS}
                    value={la}
                    onChange={update("la")}
                    placeholder="e.g. Oxfordshire County Council"
                  />
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="tpl-ref">
                    Case reference (optional)
                  </label>
                  <input
                    id="tpl-ref"
                    data-testid="tpl-ref"
                    className={INPUT_CLS}
                    value={ref}
                    onChange={update("ref")}
                    placeholder="if you have one"
                  />
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="tpl-extras">
                    SAR — any specific extras
                  </label>
                  <textarea
                    id="tpl-extras"
                    data-testid="tpl-extras"
                    className={INPUT_CLS}
                    value={extras}
                    onChange={update("extras")}
                    placeholder={"e.g. the referral document\nthe s.47 investigation report"}
                    rows={3}
                  />
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="tpl-foi">
                    FOI — replace the default topics (optional)
                  </label>
                  <textarea
                    id="tpl-foi"
                    data-testid="tpl-foi"
                    className={INPUT_CLS}
                    value={foiTopics}
                    onChange={update("foiTopics")}
                    placeholder="Leave blank for our suggested topics."
                    rows={3}
                  />
                </div>
              </div>
              {hasSaved && (
                <button
                  type="button"
                  onClick={clearForm}
                  data-testid="clear-saved-details"
                  className="mt-5 inline-flex items-center gap-2 text-xs font-accent text-ink-muted hover:text-ember transition-colors"
                >
                  <Trash2 size={12} strokeWidth={1.75} />
                  Clear my saved details
                </button>
              )}
            </div>

            <div className="mt-6 text-xs text-ink-muted font-accent leading-relaxed">
              <strong className="text-ink">Where to send them.</strong> Your
              council&rsquo;s data protection or information governance address is on
              its website — try searching &ldquo;<em>[council name] data protection officer</em>&rdquo; and &ldquo;<em>[council name] freedom of information</em>&rdquo;.
              Send the SAR and FOI as two separate emails on the same day.
            </div>
          </aside>

          {/* Templates column */}
          <div className="lg:col-span-8 space-y-8">
            <TemplateCard
              title="Subject Access Request"
              blurb="Sent to the council's data protection officer. Gets you your file — case notes, chronologies, assessments, emails about you."
              text={sar}
              kind="sar"
              filename="subject-access-request.txt"
            />
            <TemplateCard
              title="Freedom of Information Request"
              blurb="Sent to the FOI / information governance team. Gets you the system: thresholds, policies, panel terms, spend, contracts."
              text={foi}
              kind="foi"
              filename="freedom-of-information-request.txt"
            />

            <DeadlineTracker />

            <div className="border border-rule bg-linen-alt p-6 md:p-8">
              <div className={OVERLINE_CLS}>A word before you press send</div>
              <h2 className={H2_CLS + " !mt-2"}>Two things worth checking</h2>
              <ul className="space-y-3 text-sm text-ink-muted leading-relaxed">
                <li>
                  <strong className="text-ink">Are you in live proceedings?</strong>{" "}
                  If yes, tell your solicitor before sending a SAR — legal aid
                  covers advice on the timing, and specific-disclosure applications
                  through the court are often faster.
                </li>
                <li>
                  <strong className="text-ink">Read what you get carefully.</strong>{" "}
                  If a whole page is blacked out because one line mentions someone
                  else, or if you cannot see any of the professionals&rsquo; own
                  writing, that is likely over-redaction. Our{" "}
                  <Link
                    to="/understanding/records"
                    className="text-river underline underline-offset-4 hover:text-ember"
                  >
                    full guide
                  </Link>{" "}
                  explains how to push back.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
